<template>
  <nav
    class="nav max-lg:flex max-lg:flex-col max-lg:overflow-y-auto max-lg:px-[32px] max-lg:pt-55"
    :aria-labelledby="menuId"
    :class="{active: open}"
    @mouseenter="onHoverMenu"
    @mouseleave="offHoverMenu"
    id="main-menu"
  >
    <ul class="nav__list flex max-lg:justify-start" v-if="!subItemsOpened">
      <li
        v-for="(item, index) in mergedMenu"
        :key="item.handle"
        class="nav__li max-lg:border-b-beige group last:border-r-0 max-lg:border-b max-lg:px-0 max-lg:first:border-t max-lg:last:border-b-0 max-sm:!px-0 max-sm:py-[8px] lg:border-r-[2px] lg:border-r-blue"
        :class="{
          active: item.active || isOpen[index],
        }"
      >
        <div class="gap-2 flex items-center justify-between">
          <a :href="item.url" class="text-base max-sm:font-semibold">{{
            item.title
          }}</a>
          <span
            v-if="item.children.length > 0"
            class="hidden max-lg:block"
            @click.stop="toggleSubmenu(index)"
          >
            <svg
              v-if="!isOpen[index]"
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
            >
              <line y1="5" x2="9.5" y2="5" stroke="white" stroke-width="2" />
              <line
                x1="5"
                y1="2.18557e-08"
                x2="5"
                y2="9.5"
                stroke="white"
                stroke-width="2"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="2"
              viewBox="0 0 10 2"
              fill="none"
            >
              <line y1="1" x2="9.5" y2="1" stroke="white" stroke-width="2" />
            </svg>
          </span>
        </div>
        <!-- mobile lvl 1 -->
        <ul
          v-if="!subItemsOpened"
          class="shadow-header lg:bg-beige left-0 top-[103%] z-20 w-[16em] w-full overflow-hidden duration-500 ease-in-out group-hover:visible group-hover:top-[100%] group-hover:opacity-100 max-lg:h-0 max-lg:shadow-none max-lg:transition-all lg:invisible lg:absolute lg:!hidden lg:opacity-0"
          :class="{
            'mt-5 max-lg:!h-full': isOpen[index],
          }"
        >
          <li
            v-for="(subitem, subIndex) in item.children"
            :key="subIndex"
            class="max-lg:mb-8 max-lg:last:mb-0"
          >
            <div class="flex items-center justify-between">
              <a
                class="hover:bg-darkBlue block py-5 text-xs normal-case tracking-normal text-black transition-all duration-300 max-lg:font-avenirnext max-lg:text-[14px] max-lg:font-medium max-lg:text-white"
                :href="subitem.url"
                :key="index"
              >
                {{ subitem.title }}
              </a>
              <span
                v-if="subitem.children.length"
                class="hidden max-lg:block"
                @click.stop="toggleSubLevel(index, subIndex)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="7"
                  height="10"
                  viewBox="0 0 7 10"
                  fill="none"
                >
                  <path
                    d="M1 9L5 5L1 1"
                    stroke="beige"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
            </div>
          </li>
        </ul>

        <!-- desktop -->
        <div
          v-if="item.children.length > 0"
          class="shadow-header left-0 top-[109px] z-20 w-full overflow-hidden tracking-normal text-black duration-300 ease-in-out max-lg:!hidden max-lg:hidden max-lg:h-0 max-lg:shadow-none max-lg:transition-all lg:invisible lg:fixed lg:border-b-[2px] lg:border-b-blue lg:bg-white/80 lg:opacity-0"
          :class="{
            'max-lg:!h-full': isOpen[index],
            'group-hover:visible group-hover:select-all group-hover:opacity-100':
              subMenuDesktopOpen,
          }"
        >
          <div
            class="gap-4 container ml-auto flex max-w-[1450px] select-none py-30 pr-40 font-medium xl:pl-95"
          >
            <div
              v-for="(subitem, index) in subitemsWithChildren(item.children)"
              :key="index"
              class="flex-1"
            >
              <a
                class="mb-2 block text-[16px] normal-case transition-all duration-300 hover:!text-green"
                :href="subitem.url"
                :key="index"
                :class="
                  isActiveFromUrl(subitem.url)
                    ? 'text-tealPrimary font-bold'
                    : ''
                "
              >
                {{ subitem.title }}
              </a>
              <ul>
                <li
                  v-for="(sublevelitem, index) in subitem.children"
                  :key="index"
                  class="mb-3 last:mb-0"
                  :class="
                    isActiveFromUrl(sublevelitem.url)
                      ? 'text-tealPrimary font-bold'
                      : ''
                  "
                >
                  <a
                    class="hover:text-tealPrimary block text-[16px] normal-case transition-all duration-300 hover:font-bold"
                    :href="sublevelitem.url"
                    :key="index"
                  >
                    {{ sublevelitem.title }}
                  </a>
                </li>
              </ul>
            </div>

            <ul>
              <li
                v-for="(subitem, index) in subitemsWithoutChildren(
                  item.children,
                )"
                :key="index"
                class="mb-3 last:mb-0"
              >
                <a
                  class="block text-[16px] normal-case tracking-normal text-blue transition-all duration-300 hover:text-green"
                  :href="subitem.url"
                  :key="index"
                >
                  {{ subitem.title }}
                </a>
              </li>
            </ul>

            <template v-if="item.handle === 'shop'">
              <div class="flex flex-1 gap-20">
                <slot name="products"></slot>
              </div>
            </template>
          </div>
        </div>
      </li>
    </ul>
    <a
      v-if="quizUrl"
      :href="quizUrl"
      class="button button_active mt-25 min-w-[160px] text-center lg:hidden"
    >
      Take Our Quiz
    </a>
    <!-- subitems lvl 2 mobile -->
    <div v-if="subItems.children" class="mb-15">
      <div
        @click.stop="onBackLevel"
        class="mb-10 flex items-center gap-10 border-b py-[3px] font-normal uppercase"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="9"
          height="16"
          viewBox="0 0 9 16"
          fill="none"
          class="mb-5"
        >
          <path
            d="M8 15L2 8.25L8 1.5"
            stroke="beige"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        {{ subItems.title }}
      </div>
      <a
        v-for="(subitemlevel, index) in subItems.children"
        :key="index"
        class="hover:bg-darkBlue max-lg: flex items-center justify-between p-15 text-[12px] font-normal normal-case tracking-normal text-black transition-all duration-300 max-lg:px-5 max-lg:py-10 max-sm:px-0 max-sm:py-5 max-sm:font-normal"
        :href="subitemlevel.url"
      >
        {{ subitemlevel.title }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="7"
          height="10"
          viewBox="0 0 7 10"
          fill="none"
        >
          <path
            d="M1 9L5 5L1 1"
            stroke="beige"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </a>
    </div>
    <!-- links -->
    <div
      class="bg-darkBlue nav-footer sticky bottom-0 left-0 flex w-full flex-col gap-[13px] pb-80 pt-5 lg:hidden"
    >
      <!-- <a
        title="My Account Login"
        :href="accountUrl"
        class="flex items-center group gap-10 uppercase text-xs "
      >
        <span
          class="hover:border-yellow transition-all w-[30px] h-[30px] flex-[0_0_30px] rounded-full flex justify-center items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="18"
            viewBox="0 0 18 21"
            fill="none"
          >
            <circle
              cx="9"
              cy="5"
              r="4.5"
              stroke="currentColor"
              class="group-hover:stroke-yellow"
            />
            <path
              d="M0.5 18C0.5 14.4101 3.41015 11.5 7 11.5H11C14.5899 11.5 17.5 14.4101 17.5 18V20.5H0.5V18Z"
              stroke="currentColor"
              class="group-hover:stroke-yellow"
            />
          </svg>
        </span>

        MY ACCOUNT
      </a>
      <a
        v-if="show_vet"
        title="Vet Login"
        href="/pages/for-veterinarians"
        class="flex items-center gap-[8px] hover:text-yellow transition-all uppercase text-xs "
      >
        <span
          class="hover:border-yellow transition-all w-[30px] h-[30px] flex-[0_0_30px] rounded-full flex justify-center items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="17"
            viewBox="0 0 15 20"
            fill="none"
          >
            <path
              d="M2.9931 12.1735C4.09386 12.1735 4.9862 11.2811 4.9862 10.1804C4.9862 9.0796 4.09386 8.18726 2.9931 8.18726C1.89234 8.18726 1 9.0796 1 10.1804C1 11.2811 1.89234 12.1735 2.9931 12.1735Z"
              stroke="currentColor"
              class="group-hover:stroke-yellow"
              stroke-miterlimit="10"
            />
            <path
              d="M2.99316 12.1244V14.9774C2.99316 17.1289 4.73754 18.8732 6.88902 18.8732C9.04049 18.8732 10.7849 17.1289 10.7849 14.9774V10.9521"
              stroke="currentColor"
              class="group-hover:stroke-yellow"
              stroke-miterlimit="10"
            />
            <path
              d="M9.52432 1H7.82567C7.40742 1 7.06836 1.33906 7.06836 1.75731V4.66498C7.06836 6.27217 7.69964 7.81356 8.82612 8.96012L10.7846 10.951"
              stroke="currentColor"
              class="group-hover:stroke-yellow"
              stroke-miterlimit="10"
            />
            <path
              d="M12.044 1H13.7427C14.1609 1 14.5 1.33906 14.5 1.75731V4.66498C14.5 6.27217 13.8687 7.81356 12.7422 8.96012L10.7837 10.951"
              stroke="currentColor"
              class="group-hover:stroke-yellow"
              stroke-miterlimit="10"
            />
          </svg>
        </span>
        Vet Login
      </a> -->

      <!-- products -->
      <slot name="products"></slot>
    </div>
  </nav>
  <div
    class="icon-parent flex items-center justify-center rounded-full sm:h-[34px] sm:w-[34px] sm:flex-[0_0_48px] lg:hidden"
  >
    <button
      class="icon-menu"
      :class="{_active: open}"
      @click="openMenu"
      aria-label="Menu"
    >
      <span></span>
      <span></span>
      <span></span>
    </button>
  </div>
</template>

<script>
export default {
  name: "SiteNavigation",
  props: {
    menuId: {
      type: String,
      required: true,
    },
    menu: {
      type: Array,
      required: true,
    },
    menu_right: {
      type: Array,
      required: true,
    },
    show_vet: {
      type: Boolean,
      required: true,
    },
    accountUrl: {
      type: String,
      default: "/pages/account-login",
    },
    quizUrl: {
      type: String,
      default: "",
    },
  },
  data: () => {
    return {
      open: false,
      isOpen: [],
      width: typeof window !== "undefined" ? window.innerWidth : 0,
      parentIndex: null,
      subMenuIndex: null,
      subItemsOpened: false,
      subMenuDesktopOpen: false,
    };
  },
  computed: {
    mergedMenu() {
      return this.width <= 1024 && !this.show_vet && this.menu_right
        ? [...this.menu, ...this.menu_right]
        : this.menu;
    },
    subItems() {
      return (
        this.mergedMenu[this.parentIndex]?.children[this.subMenuIndex] || []
      );
    },
  },
  methods: {
    handleResize() {
      this.width = window.innerWidth;
      this.isOpen = new Array(this.mergedMenu.length).fill(false);
      this.onBackLevel();
    },
    preventScroll(e) {
      const nav = this.$el;
      if (nav && nav.contains(e.target)) {
        // Allow scroll inside nav, but prevent it from bubbling to body
        if (nav.scrollHeight > nav.clientHeight) return;
      }
      e.preventDefault();
    },
    openMenu(event) {
      if (!this.open) {
        event.stopPropagation();
        this.open = true;
        document.querySelector("html").classList.add("menu-open");
        document.addEventListener("click", this.closeMenuOutside);
        document.addEventListener("touchmove", this.preventScroll, {
          passive: false,
        });
        document.addEventListener("wheel", this.preventScroll, {
          passive: false,
        });
      } else {
        this.closeMenuOutside(event);
      }
    },
    closeMenuOutside(event) {
      const nav = document.querySelector(".nav");
      const icon = document.querySelector(".nav__close");
      if (!nav.contains(event.target) || event.target === icon) {
        this.open = false;
        document.querySelector("html").classList.remove("menu-open");
        document.removeEventListener("click", this.closeMenuOutside);
        document.removeEventListener("touchmove", this.preventScroll);
        document.removeEventListener("wheel", this.preventScroll);
      }
    },
    toggleSubmenu(index) {
      this.isOpen[index] = !this.isOpen[index];
    },
    subitemsWithChildren(obj) {
      return obj.filter(
        subitem => subitem.children && subitem.children.length > 0,
      );
    },
    subitemsWithoutChildren(obj) {
      return obj.filter(
        subitem => !subitem.children || subitem.children.length === 0,
      );
    },
    onHoverMenu() {
      const isAtTop =
        window.scrollY === 0 || document.documentElement.scrollTop === 0;
      if (isAtTop) {
        document.querySelector("html").classList.add("no-scroll");
        this.subMenuDesktopOpen = true;
      }
    },
    offHoverMenu() {
      if (!this.subMenuDesktopOpen) return;

      document.querySelector("html").classList.remove("no-scroll");
      this.subMenuDesktopOpen = false;
    },
    toggleSubLevel(parentIndex, subIndex) {
      this.parentIndex = parentIndex;
      this.subMenuIndex = subIndex;
      this.subItemsOpened = true;
    },
    onBackLevel() {
      this.parentIndex = null;
      this.subMenuIndex = null;
      this.subItemsOpened = false;
    },
    isActiveFromUrl(url) {
      let targetSegment = "";

      try {
        const targetPath = new URL(url, window.location.origin).pathname;
        targetSegment = targetPath.split("/").filter(Boolean).pop();
      } catch (e) {
        targetSegment = url.split("/").filter(Boolean).pop();
      }

      const currentPath = window.location.pathname;
      const currentSegment = currentPath.split("/").filter(Boolean).pop();
      return currentSegment === targetSegment;
    },
  },
  mounted() {
    this.isOpen = new Array(this.mergedMenu.length).fill(false);
    if (this.mergedMenu.length > 0 && this.mergedMenu[0].children.length > 0) {
      this.isOpen[0] = true;
    }
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("scroll", this.offHoverMenu);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.offHoverMenu);
  },
};
</script>
<style>
@media (max-height: 700px) {
  .nav-footer {
    @apply !static;
  }
}
/* .menu-open .header-logo {
  @apply max-sm:w-[74px] max-sm:h-55 max-sm:translate-y-0;
} */
/* bg-blue is now set in header.scss */
.menu-open .header-basket {
  @apply relative z-50;
}
</style>
